import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings, ChevronDown } from "lucide-react";

const KEY = "triptap.cookie.consent";

/**
 * Cookie preferences banner. Persists a JSON blob:
 *   { essential: true, analytics: bool, all: bool, at: iso }
 * Essential are always on. On first visit the slim banner appears; clicking
 * "Customize" expands three toggles. Choice remembered across visits.
 */
export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: true, all: true });

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) {
        const t = setTimeout(() => setShow(true), 800);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  const persist = (p) => {
    try { localStorage.setItem(KEY, JSON.stringify({ ...p, at: new Date().toISOString() })); } catch {}
    setShow(false);
  };
  const acceptAll = () => persist({ essential: true, analytics: true, all: true });
  const rejectAll = () => persist({ essential: true, analytics: false, all: false });
  const saveChoice = () => persist(prefs);

  if (!show) return null;
  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[9999]"
      data-testid="cookie-banner"
    >
      <div className="glass-card gold-border rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-500/15 border border-red-400/30 grid place-items-center flex-shrink-0">
            <Cookie className="w-4 h-4 text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-200 leading-relaxed">
              We use cookies for a smooth ride experience. See{" "}
              <Link to="/privacy#section-8" className="text-red-300 hover:text-red-200 underline underline-offset-2" data-testid="cookie-banner-link">
                §8 of our Privacy Policy
              </Link>.
            </p>

            {expanded && (
              <div className="mt-3 space-y-2 border-t border-white/10 pt-3" data-testid="cookie-prefs-panel">
                <PrefRow
                  testid="cookie-pref-essential"
                  label="Essential" desc="Login sessions, security, form submission — always on."
                  checked disabled
                />
                <PrefRow
                  testid="cookie-pref-analytics"
                  label="Analytics" desc="Anonymous usage stats to improve the app."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v, all: v && p.all }))}
                />
                <PrefRow
                  testid="cookie-pref-all"
                  label="All (marketing)" desc="Personalized promos, referral tracking."
                  checked={prefs.all}
                  onChange={(v) => setPrefs((p) => ({ ...p, all: v, analytics: v || p.analytics }))}
                />
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-3">
              {!expanded && (
                <button onClick={acceptAll} className="btn-gold rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide" data-testid="cookie-accept-all">
                  Accept all
                </button>
              )}
              {expanded && (
                <button onClick={saveChoice} className="btn-gold rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide" data-testid="cookie-save-choice">
                  Save my choice
                </button>
              )}
              <button onClick={rejectAll} className="rounded-full px-4 py-1.5 text-xs font-mono uppercase tracking-wide bg-transparent border border-white/10 text-slate-300 hover:bg-white/5" data-testid="cookie-reject-all">
                Essential only
              </button>
              <button
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wide text-slate-400 hover:text-red-300 ml-auto"
                data-testid="cookie-customize"
              >
                <Settings className="w-3 h-3" /> {expanded ? "Hide" : "Customize"}
                <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>
          <button onClick={rejectAll} className="text-slate-500 hover:text-white flex-shrink-0" aria-label="Dismiss" data-testid="cookie-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const PrefRow = ({ label, desc, checked, disabled, onChange, testid }) => (
  <label className={`flex items-start gap-2.5 cursor-pointer ${disabled ? "opacity-70 cursor-not-allowed" : ""}`} data-testid={testid}>
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange && onChange(e.target.checked)}
      className="mt-0.5 accent-red-500 w-4 h-4"
    />
    <span className="flex-1 min-w-0">
      <span className="text-white text-xs font-semibold block">{label}{disabled && <span className="ml-1.5 text-[9px] font-mono uppercase tracking-wider text-slate-500">required</span>}</span>
      <span className="text-[11px] text-slate-500 leading-snug">{desc}</span>
    </span>
  </label>
);
